<template>
  <v-dialog v-model="dialog" max-width="780px" persistent>
    <v-card>
      <v-card-title>Planejamentos Semestrais</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-select
              v-model="formSemestral.semestre"
              :items="$constants.semestres"
              item-text="descricao"
              item-value="id"
              label="Selecione o Semestre"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <v-file-input
              v-model="formSemestral.pdf"
              accept=".pdf"
              label="Selecione o PDF (máximo de: 2MB)"
              outlined
              dense
            ></v-file-input>
          </v-col>
        </v-row>
        <v-btn
          color="success"
          block
          @click="submitPlanejamentoSemestral"
          :loading="submittingSemestral"
          :disabled="!formSemestral.pdf || !formSemestral.semestre"
        >
          Realizar Upload
        </v-btn>

        <v-divider class="my-4"></v-divider>

        <v-simple-table v-if="planejamentosSemestrais.length > 0">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">#</th>
                <th class="text-left">Semestre</th>
                <th class="text-left">Arquivo</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in planejamentosSemestrais" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.semestre }}º Semestre</td>
                <td>{{ item.nome_arquivo }}</td>
                <td class="text-center">
                  <v-btn
                    x-small
                    class="ml-1"
                    color="primary"
                    @click="baixarPlanejamentoSemestral(item)"
                    title="Baixar PDF"
                  >
                    <v-icon small>fa fa-download</v-icon>
                  </v-btn>
                  <v-btn
                    x-small
                    class="ml-1"
                    color="error"
                    @click="deletarPlanejamentoSemestral(item)"
                    title="Excluir"
                  >
                    <v-icon small>fa fa-trash</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div v-else class="text-center py-4 gray--text">Nenhum planejamento anexado.</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('dialogChange', false)">Fechar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    matricula_id: {
      type: [Number, String],
      required: true,
    },
    gestao_id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      submittingSemestral: false,
      planejamentosSemestrais: [],
      formSemestral: {
        semestre: null,
        pdf: null,
      },
      rules: {
        tamanhoMaximo: (file) =>
          !file || file.size <= 2 * 1024 * 1024 || "O limite do PDF para ser inserido é de 2MB",
        apenasPdf: (file) =>
          !file || file.type === "application/pdf" || "O arquivo deve ser um PDF",
      },
    };
  },
  watch: {
    dialog(val) {
      if (val) {
        this.loadPlanejamentosSemestrais();
      } else {
        this.formSemestral.pdf = null;
        this.formSemestral.semestre = null;
      }
    },
  },
  methods: {
    async loadPlanejamentosSemestrais() {
      try {
        this.planejamentosSemestrais =
          await this.$services.educacaoInclusivaService.carregarPlanejamentosSemestrais(
            this.matricula_id,
            this.gestao_id
          );
      } catch (error) {
        this.$handleError(error);
      }
    },
    async submitPlanejamentoSemestral() {
      const planejamentoExistente = this.planejamentosSemestrais.find(
        (p) => p.semestre === this.formSemestral.semestre
      );

      if (planejamentoExistente) {
        this.$modals.warn({
          title: "Atenção!",
          message: `Já existe um planejamento para o ${this.formSemestral.semestre}º Semestre. Deseja substituí-lo?`,
          buttons: [
            {
              text: "Cancelar",
              callback: () => {
                this.$modals.close();
              },
              props: {
                color: "error",
                small: true,
              },
            },
            {
              text: "Substituir",
              callback: () => {
                this.$modals.close();
                this.realizarUpload();
              },
              props: {
                color: "success",
                small: true,
              },
            },
          ],
        });
      } else {
        this.realizarUpload();
      }
    },
    async realizarUpload() {
      if (this.formSemestral.pdf && this.formSemestral.pdf.size > 2 * 1024 * 1024) {
        this.$toast.danger("O limite do PDF para ser inserido é de 2MB");
        return;
      }
      this.submittingSemestral = true;
      try {
        const formData = new FormData();
        formData.append("file", this.formSemestral.pdf);
        formData.append("semestre", this.formSemestral.semestre);
        formData.append("matricula_id", this.matricula_id);
        formData.append("gestao_id", this.gestao_id);

        await this.$services.educacaoInclusivaService.salvarPlanejamentoSemestral(formData);
        this.$toast.success("Planejamento anexado com sucesso!");
        this.formSemestral.pdf = null;
        this.loadPlanejamentosSemestrais();
      } catch (error) {
        this.$handleError(error.error || error);
      } finally {
        this.submittingSemestral = false;
      }
    },
    async deletarPlanejamentoSemestral(item) {
      this.$modals.danger({
        title: "Cuidado!",
        message: "Tem certeza que deseja excluir este planejamento?",
        confirmationCode: item.id,
        confirmationMessage: `Por favor, digite <strong>${item.id}</strong> para confirmar`,
        buttons: [
          {
            text: "Cancelar",
            callback: () => {
              this.$modals.close();
            },
            props: {
              color: "warning",
              small: true,
            },
          },
          {
            text: "Confirmar",
            disableIfCodeDoesntMatch: true,
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Excluindo planejamento...");
              try {
                await this.$services.educacaoInclusivaService.deletarPlanejamentoSemestral(item.id);
                this.$toast.success("Planejamento excluído com sucesso!");
                this.loadPlanejamentosSemestrais();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            props: {
              color: "error",
              small: true,
            },
          },
        ],
      });
    },
    async baixarPlanejamentoSemestral(item) {
      try {
        await this.$services.educacaoInclusivaService.baixarPlanejamentoSemestral(
          item.id,
          item.nome_arquivo
        );
      } catch (error) {
        this.$handleError(error);
      }
    },
  },
};
</script>
